import PropTypes from "prop-types";
import { debounce } from "lodash";
import { usePopper } from "react-popper";
import { SIZE, WIDTH } from "../constants";
import { useTranslation } from "react-i18next";
import { useState, useMemo, useEffect } from "react";
import { Dropdown, Input, MenuItem, Menu } from "../atoms";
import { IconSearch } from "@tabler/icons-react";

const propTypes = {
  options: PropTypes.array,
  className: PropTypes.string,
  onItemSelect: PropTypes.func,
  isItemDisabled: PropTypes.bool,
  inputClassName: PropTypes.string,
  isDisabledDropdown: PropTypes.bool,
  size: PropTypes.oneOf([SIZE.sm, SIZE.md, SIZE.lg]),
  width: PropTypes.oneOf([WIDTH.xs, WIDTH.sm, WIDTH.md, WIDTH.lg, WIDTH.xl]),
};

function Combobox({
  size,
  options,
  className,
  onItemSelect,
  inputClassName,
  isItemDisabled,
  width = WIDTH.lg,
  isDisabledDropdown,
  ...props
}) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [matchingOptions, setMatchingOptions] = useState([]);
  const [popperElement, setPopperElement] = useState(null);
  const [referenceElement, setReferenceElement] = useState(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "bottom",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 10],
        },
      },
    ],
    strategy: "absolute",
  });

  const filterMatchingOptions = useMemo(
    () =>
      debounce((value) => {
        if (value?.length > 0) {
          const valueRegexp = new RegExp(value, "gui");
          setMatchingOptions(
            options?.filter(({ value }) => value.match(valueRegexp)) || []
          );
        } else {
          setMatchingOptions([]);
        }
        setIsLoading(false);
      }, 250),
    [options]
  );

  useEffect(() => {
    setIsLoading(true);
    filterMatchingOptions(inputValue);

    return () => filterMatchingOptions.cancel();
  }, [filterMatchingOptions, inputValue]);

  const renderOptions = () => {
    if (isLoading) {
      return <MenuItem disabled text={t("dropdown.loading_items")} />;
    }

    if (matchingOptions?.length === 0) {
      return <MenuItem disabled text={t("dropdown.no_matches_found")} />;
    }

    return matchingOptions?.map(({ value, supportingText, icon }, index) => (
      <MenuItem
        icon={icon}
        text={value}
        value={value}
        disabled={isItemDisabled}
        supportingText={supportingText}
        key={`menu-item-combobox-${index}`}
      />
    ));
  };

  const handleSelectItem = (value) => {
    setInputValue(value);
    onItemSelect(value);
  };

  const handleInputChange = (value) => {
    setInputValue(value);
    if (!value?.length) {
      onItemSelect("");
    }
  };

  return (
    <Dropdown
      size={size}
      width={width}
      className={className}
      inputValue={inputValue}
      isDisabled={isDisabledDropdown}
      onSelect={(value) => handleSelectItem(value)}
      onInputValueChange={(value) => handleInputChange(value)}
    >
      <Input
        type="search"
        inputRef={setReferenceElement}
        inputClassName={inputClassName}
        iconLeft={<IconSearch />}
        {...props}
      />
      <Menu
        scrollable
        style={styles.popper}
        menuRef={setPopperElement}
        {...attributes.popper}
      >
        {renderOptions()}
      </Menu>
    </Dropdown>
  );
}

Combobox.propTypes = propTypes;
export default Combobox;
